import { forwardRef } from 'react';
import { useTippy } from '@hooks/useTippy';
import { PopoverContainer } from './PopoverContainer';
import type { PopoverContainerProps } from './PopoverContainer';
import { PopoverContent } from './PopoverContent';
import type { BasePopoverContentProps } from './PopoverContent';
import { ZINDEX } from '@utils/constants';

export type ExposedPopoverContainerProps = Omit<
  PopoverContainerProps,
  'content' | 'onCreate'
>;

export interface BasePopoverProps
  extends ExposedPopoverContainerProps,
    Omit<BasePopoverContentProps, 'onDismiss' | 'children'> {
  content?: BasePopoverContentProps['children'];
  /**
   * Trigger Popover by click or by hover.
   */
  trigger?: 'click' | 'mouseenter focus';
}

/**
 * BasePopover component
 *
 * **WARNING!** _This component is for internal use in the Design System. If you
 * need to use it for a custom component, please discuss your use case with
 * the Design System squad before proceeding, as they may be able to help
 * you with a better solution._
 *
 * @summary Provides a low-level implementation for the Popover component.
 * @see Popover
 */
export const BasePopover = forwardRef<HTMLElement, BasePopoverProps>(
  (
    {
      title,
      icon,
      content,
      children,
      actionText,
      onActionPress,
      withCloseButton,
      ...tippyProps
    }: BasePopoverProps,
    ref,
  ): JSX.Element => {
    const tippyInstance = useTippy();

    return (
      <PopoverContainer
        ref={ref}
        onCreate={tippyInstance.set}
        content={
          <PopoverContent
            title={title}
            icon={icon}
            actionText={actionText}
            onActionPress={onActionPress}
            onDismiss={tippyInstance.close}
            withCloseButton={withCloseButton}
          >
            {content}
          </PopoverContent>
        }
        {...tippyProps}
      >
        {children}
      </PopoverContainer>
    );
  },
);

BasePopover.displayName = 'BasePopover';

BasePopover.defaultProps = {
  zIndex: ZINDEX.POPOVER,
  placement: 'top',
  appendTo: () => document.body,
  withCloseButton: true,
};
